// Hook : préférences utilisateur persistées en DB (vague 43).
// Pattern get/set avec cache TanStack — supporte multi-device.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/authStore'

export const useUserPreference = <T,>(key: string, defaultValue: T) => {
  const profile = useAuthStore((s) => s.profile)
  const userId = profile?.id ?? null
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['user-preferences', userId, key],
    queryFn: async (): Promise<T> => {
      if (!userId) return defaultValue
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('user_id', userId)
        .eq('key', key)
        .maybeSingle()
      if (error) throw error
      const row = data as { value: unknown } | null
      return row ? (row.value as T) : defaultValue
    },
    enabled: Boolean(userId),
    staleTime: 60_000,
  })

  const setMut = useMutation({
    mutationFn: async (value: T) => {
      if (!userId) throw new Error('Non authentifié')
      const { error } = await supabase
        .from('user_preferences')
        .upsert(
          {
            user_id: userId,
            key,
            value: value as unknown,
          } as never,
          { onConflict: 'user_id,key' },
        )
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-preferences', userId, key] })
    },
  })

  return {
    value: (q.data ?? defaultValue) as T,
    setValue: (v: T) => setMut.mutate(v),
    isLoading: q.isLoading,
    isSaving: setMut.isPending,
  }
}
